import { showErrorToast } from "./Toast";
import CRYPTO from "crypto-js";
import { environment } from "../../../environments/environment";

export function validateEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

/** Split daily-hours notification field (comma, semicolon, or newline). */
export function splitDailyHoursNotifyEmails(raw) {
  if (raw == null || String(raw).trim() === "") return [];
  return String(raw)
    .split(/[,;\n\r]+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

const MAX_DAILY_HOURS_NOTIFY_EMAILS = 25;

/** True if empty, or every segment is a valid email and count is within limit. */
export function validateDailyHoursNotifyEmailList(raw) {
  const parts = splitDailyHoursNotifyEmails(raw);
  if (!parts.length) return true;
  if (parts.length > MAX_DAILY_HOURS_NOTIFY_EMAILS) return false;
  return parts.every((p) => validateEmail(p));
}

/** Case-insensitive order-insensitive compare for dirty-check. */
export function dailyHoursNotifyEmailsDiffer(a, b) {
  const key = (s) =>
    splitDailyHoursNotifyEmails(s)
      .map((x) => x.toLowerCase())
      .sort()
      .join("\n");
  return key(a) !== key(b);
}

/** Split leave-request notification field (comma, semicolon, or newline). */
export const splitLeaveRequestNotifyEmails = splitDailyHoursNotifyEmails;

/** True if empty, or every segment is a valid email and count is within limit. */
export const validateLeaveRequestNotifyEmailList = validateDailyHoursNotifyEmailList;

/** Case-insensitive order-insensitive compare for leave-request notify dirty-check. */
export const leaveRequestNotifyEmailsDiffer = dailyHoursNotifyEmailsDiffer;
export function validatePhoneNumber(phone) {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/; // E.164 format (International)
  return phoneRegex.test(phone);
}

export const showErrorResponse = (error) => {
  const defaultMessage = "Something went wrong! please try again later";
  const responseCode =
    error?.responseCode ??
    error?.response?.data?.error?.responseCode ??
    error?.response?.data?.responseCode;
  const message =
    error?.message ??
    error?.response?.data?.error?.message ??
    error?.response?.data?.message;

  if (responseCode !== undefined && responseCode !== null) {
    if (responseCode.toString() === "1" && message) {
      showErrorToast(message);
      return;
    }
    if (responseCode.toString() === "-1") {
      return;
    }
  }

  showErrorToast(message || defaultMessage);
};

const getStore = () => {
  const store = require("../../../Redux/store").store;
  return store;
};

/** Normalize DB/user profile moduleKey (JSON string or array) to string[]. */
const normalizeModuleKeys = (moduleKey) => {
  if (Array.isArray(moduleKey)) return moduleKey.map(String);
  if (typeof moduleKey === "string") {
    try {
      const p = JSON.parse(moduleKey);
      return Array.isArray(p) ? p.map(String) : [moduleKey];
    } catch {
      return [moduleKey];
    }
  }
  return [];
};

/** Profile/API may return moduleId as string, number, or BigInt — avoid `!==` false negatives. */
const moduleIdsMatch = (a, b) => {
  if (a == null || b == null) return false;
  const na = Number(a);
  const nb = Number(b);
  return !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
};

export const checkPermission = (module_id, incoming_permission) => {
  const authState = getStore()?.getState?.()?.auth?.auth;
  const permissions = authState?.user?.permissions;

  if (!Array.isArray(permissions) || incoming_permission == null || incoming_permission === "") return false;

  const needle = String(incoming_permission).trim();
  return permissions.some((permission) => {
    if (!moduleIdsMatch(permission?.moduleId, module_id)) return false;
    const keys = normalizeModuleKeys(permission?.moduleKey);
    return keys.some((k) => String(k).trim() === needle);
  });
};

/** True if the role has any of the listed permission keys on that module (exact match). */
export const checkAnyPermission = (module_id, incoming_permissions) => {
  if (!Array.isArray(incoming_permissions) || !incoming_permissions.length) return false;
  return incoming_permissions.some((p) => checkPermission(module_id, p));
};

export const permissionType = {
  USER_MANAGEMENT: 1,
  PROJECT_MANAGEMENT: 2,
  PROJECT_DOCUMENTATION: 10,
  /** Company policy — read (view), create (first upload), update (replace), delete. */
  COMPANY_POLICY: 11,
  DAILY_HOURS: 3,
  ATTENDANCE: 7,
  LEAVE_REQUEST: 4,
  LEAVE_APPROVE: 5,
  ROLE_MANAGEMENT: 6,
  /** Own WFH requests (stored as leaveRequests with WFH markers). */
  WFH_REQUESTS: 8,
  /** Shared dashboard praise — "create" allows posting/updating the message. */
  PRAISE_MESSAGES: 9,
  /** Full admin UI/API access when key "admin access" is assigned (CEO, CMO, etc.). */
  ADMIN_ACCESS: 12,
  /** Company details — profile, tax, banking, and signature details. */
  COMPANY_DETAILS: 13,
  /** Client management — onboarding, profile updates, and resource allocation. */
  CLIENT_MANAGEMENT: 14,
  /** Invoice management — create, view, update, and delete invoices. */
  INVOICE_MANAGEMENT: 15,
  /** Finance management — salaries, sheets, deductions, and reports. */
  FINANCE_MANAGEMENT: 16,
  /** Finance upload document tab — view, upload, and delete files. */
  FINANCE_DOCUMENTS: 19,
  /** Finance expenses management — general expenses + office_* sub-permissions. */
  FINANCE_EXPENSES: 20,
  /** Company operations — public holidays CRUD and PDF import. */
  COMPANY_OPS: 17,
  /** Salary slips — view all, view own, download PDF, send email (generated when salary is paid). */
  SALARY_SLIP: 18,
  /** SOPs — upload, assign to employees; employees view assigned SOPs only. */
  SOP_MANAGEMENT: 21,
  /** Offboarding documents — upload .docx, assign, employees submit filled document. */
  OFFBOARDING_DOCUMENTS: 22,
  /** Project credentials — shared per project + personal credentials. */
  PROJECT_CREDENTIALS: 23,
  /** Performance criteria — late arrival warnings and half-day deductions. */
  PERFORMANCE_CRITERIA: 24,
  /** Employee time tracker — check-in/out and admin monitoring. */
  TIME_TRACKER: 25,
};

/** Upload / assign SOPs (non-employee managers). */
export const canManageSops = (user) =>
  hasAdminAccess(user) ||
  checkPermission(permissionType.SOP_MANAGEMENT, "create") ||
  checkPermission(permissionType.SOP_MANAGEMENT, "assign employee");

export const canReadSops = () => checkPermission(permissionType.SOP_MANAGEMENT, "read");

export const canReadOffboardingDocuments = () =>
  checkPermission(permissionType.OFFBOARDING_DOCUMENTS, "read");

export const canReadProjectCredentials = () =>
  checkPermission(permissionType.PROJECT_CREDENTIALS, "read");

export const canCreateProjectCredentials = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "create");

export const canUpdateProjectCredentials = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "update");

export const canDeleteProjectCredentials = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "delete");

export const canManagePersonalCredentials = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "manage personal");

export const canShareProjectCredentials = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "share credential");

export const canReadProjectCredentialFiles = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "read project files");

export const canReadSharedCredentialFiles = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "read shared files");

export const canUploadProjectCredentialFiles = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "upload project files");

export const canUploadSharedCredentialFiles = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "upload shared files");

export const canDeleteProjectCredentialFiles = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "delete project files");

export const canDeleteSharedCredentialFiles = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PROJECT_CREDENTIALS, "delete shared files");

export const canAccessProjectCredentialsPage = (user) =>
  hasAdminAccess(user) ||
  canReadProjectCredentials() ||
  canReadProjectCredentialFiles(user) ||
  canReadSharedCredentialFiles(user) ||
  canManagePersonalCredentials(user);

export const canReadPerformanceCriteria = () =>
  checkPermission(permissionType.PERFORMANCE_CRITERIA, "read");

export const canReadAllPerformanceCriteria = (user) =>
  hasAdminAccess(user) ||
  checkPermission(permissionType.PERFORMANCE_CRITERIA, "read users");

export const canSendPerformanceCriteriaEmail = (user) =>
  hasAdminAccess(user) ||
  checkPermission(permissionType.PERFORMANCE_CRITERIA, "send mail") ||
  checkPermission(permissionType.PERFORMANCE_CRITERIA, "read users");

export const canReadTimeTracker = () =>
  checkPermission(permissionType.TIME_TRACKER, "read");

export const canReadTimeTrackerAdmin = (user) =>
  hasAdminAccess(user) ||
  checkPermission(permissionType.TIME_TRACKER, "admin read");

/** Upload / assign offboarding documents (non-employee managers). */
export const canManageOffboardingDocuments = (user) =>
  hasAdminAccess(user) ||
  checkPermission(permissionType.OFFBOARDING_DOCUMENTS, "create") ||
  checkPermission(permissionType.OFFBOARDING_DOCUMENTS, "assign employee");

/** Built-in Admin role (id 1) — account-level rules only; prefer hasAdminAccess for product features. */
export const isBuiltInAdminRole = (user) => Number(user?.roleId) === 1;

/** Admin role or ADMIN_ACCESS "admin access" permission — same dashboard/API capabilities as Admin. */
export const hasAdminAccess = (user) =>
  isBuiltInAdminRole(user) ||
  checkPermission(permissionType.ADMIN_ACCESS, "admin access");

/** Org-wide salary slip list (HR / roles with SALARY_SLIP read). */
export const canReadSalarySlips = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.SALARY_SLIP, "read");

/** Download any employee's salary slip PDF. */
export const canDownloadSalarySlips = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.SALARY_SLIP, "download");

/** View and download own payslip (My Salary Slips). */
export const canViewOwnSalarySlips = (user) =>
  !hasAdminAccess(user) && checkPermission(permissionType.SALARY_SLIP, "view own");

/** Sidebar "My Salary Slips". */
export const canViewOwnSalarySlipsNav = (user) => {
  if (!user?.id && !user?.userId) return false;
  return canViewOwnSalarySlips(user);
};

/** Personal payslip page: own slips only (`/my-salary-slips`). */
export const isOwnSalarySlipPageMode = (user, pathname = "") => {
  if (!canViewOwnSalarySlips(user)) return false;
  if (String(pathname || "").includes("/my-salary-slips")) return true;
  return !canReadSalarySlips(user);
};

export const canViewSalarySlips = (user) =>
  canReadSalarySlips(user) || canViewOwnSalarySlips(user);

export const canAccessSalarySlipDownload = (user) =>
  canDownloadSalarySlips(user) || canViewOwnSalarySlips(user);

/** @deprecated Alias for hasAdminAccess */
export const isAdminUser = hasAdminAccess;

/** Role ids from backend (`ROLE` in tcs/src/constants/const.js). */
export const CMO_ROLE_ID = 2;
export const HR_ROLE_ID = 3;
export const EMPLOYEE_ROLE_ID = 4;
export const CEO_ROLE_ID = 5;

/**
 * Parenthetical note for admin “missed daily updates” when employee had approved leave.
 * Maps leaveType from API (Full Leave, Half Leave, Early Leave, etc.).
 */
export const getMissedDailyUpdatesLeaveSuffix = (leaveType) => {
  const t = String(leaveType ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
  if (t === "half leave" || t.includes("half day") || t === "half day") {
    return "half day";
  }
  if (t === "early leave" || t.includes("early leave")) {
    return "early leave";
  }
  return "on leave";
};

/** CEO and CMO are excluded from team daily-update compliance lists. */
export const isCeoOrCmoRoleUser = (profile) => {
  const roleId = Number(profile?.roleId);
  if (roleId === CMO_ROLE_ID || roleId === CEO_ROLE_ID) return true;
  const role = String(profile?.role ?? profile?.roleName ?? "")
    .trim()
    .toLowerCase();
  return role === "ceo" || role === "cmo";
};

/** Admin / executive roles excluded from team daily-work update compliance stats. */
export const isExcludedFromDailyWorkCompliance = (profile) => {
  if (isCeoOrCmoRoleUser(profile)) return true;
  if (isBuiltInAdminRole(profile)) return true;
  const role = String(profile?.role ?? profile?.roleName ?? "")
    .trim()
    .toLowerCase();
  return role === "admin";
};

export const filterDailyWorkComplianceTeam = (profiles) =>
  (Array.isArray(profiles) ? profiles : []).filter(
    (profile) => !isExcludedFromDailyWorkCompliance(profile)
  );

export const getDailyWorkComplianceTeamUserIds = (profiles) =>
  filterDailyWorkComplianceTeam(profiles)
    .map((emp) => String(emp?.userId ?? emp?.id ?? ""))
    .filter(Boolean);

/** True when a directory row is HR or Employee (password may be shown). */
export const isHrOrEmployeeRoleUser = (profile) => {
  const roleId = Number(profile?.roleId);
  if (roleId === HR_ROLE_ID || roleId === EMPLOYEE_ROLE_ID) return true;
  const role = String(profile?.role ?? profile?.roleName ?? "")
    .trim()
    .toLowerCase();
  return role === "hr" || role === "employee";
};

/** Show password on Employees list only for HR / Employee roles; mask others. */
export const displayEmployeePassword = (password, profile) => {
  if (!isHrOrEmployeeRoleUser(profile)) return "*********";
  return password ?? "";
};

/** Post team praise: admins always; others need PRAISE_MESSAGES "create". */
export const canPostTeamPraise = (user) =>
  hasAdminAccess(user) || checkPermission(permissionType.PRAISE_MESSAGES, "create");

/** Same rules as backend `isWfhLeaveRecord` — list row / edit target. */
export const isWfhLeaveRow = (leave) => {
  const wl = String(leave?.workLocation ?? "").trim().toLowerCase();
  if (wl === "wfh" || wl === "work from home") return true;
  const lt = String(leave?.leaveType ?? "").trim().toUpperCase();
  const lc = String(leave?.leaveCategory ?? "").trim().toUpperCase();
  if (lt === "WFH" || lc === "WFH") return true;
  const ltLower = String(leave?.leaveType ?? "").trim().toLowerCase();
  const lcLower = String(leave?.leaveCategory ?? "").trim().toLowerCase();
  if (ltLower === "wfh" || lcLower === "wfh") return true;
  if (ltLower.includes("work from home") || lcLower.includes("work from home")) return true;
  return false;
};

export const canReadLeaveManagement = () =>
  checkPermission(permissionType.LEAVE_REQUEST, "read") ||
  checkPermission(permissionType.WFH_REQUESTS, "read");

/** HR/Admin: create approved WFH for another employee. */
export const canAssignWfhToEmployee = () =>
  checkPermission(permissionType.WFH_REQUESTS, "assign employee");

export const canReadProjectDocumentation = () =>
  checkPermission(permissionType.PROJECT_DOCUMENTATION, "read");

const ATT_KEYS = [
  "read",
  "read users",
  "update",
  "overview_read",
  "overview_write",
  "overview_update",
  "history_read",
  "history_write",
  "history_update",
  "team_read",
  "team_write",
  "team_update",
  "integration_read",
  "integration_write",
  "integration_update",
];

const getAuthUser = () => getStore()?.getState?.()?.auth?.auth?.user;

/** User can open the Attendance area at all (sidebar + /attendance route). */
export const hasAttendanceModuleAccess = () => {
  const user = getAuthUser();
  if (hasAdminAccess(user)) return true;
  return checkAnyPermission(permissionType.ATTENDANCE, ATT_KEYS);
};

export const canAttendanceOverview = () =>
  checkAnyPermission(permissionType.ATTENDANCE, ["overview_read", "read", "read users"]);

export const canAttendanceHistory = () =>
  checkAnyPermission(permissionType.ATTENDANCE, ["history_read", "read", "read users"]);

export const canAttendanceTeam = () =>
  checkAnyPermission(permissionType.ATTENDANCE, ["team_read", "read users"]);

/** Team day-status manual edits (HR/Admin with explicit permission). */
export const canAttendanceTeamStatusEdit = () =>
  checkAnyPermission(permissionType.ATTENDANCE, ["team_status_update", "team_update", "update"]);

export const canAttendanceIntegration = () =>
  checkAnyPermission(permissionType.ATTENDANCE, [
    "integration_read",
    "integration_write",
    "integration_update",
    "update",
  ]);

/** Config / sync / machine write operations */
export const canAttendanceIntegrationMutate = () =>
  checkAnyPermission(permissionType.ATTENDANCE, ["integration_write", "integration_update", "update"]);

/** History rollup (all employees) — HR / admin access */
export const canAttendanceReadAllEmployees = () => {
  const user = getAuthUser();
  if (hasAdminAccess(user)) return true;
  return checkAnyPermission(permissionType.ATTENDANCE, ["read users", "history_read"]);
};

/** Machine / integration logs API (GET /logs). */
export const canAttendanceMachineLogs = () =>
  checkAnyPermission(permissionType.ATTENDANCE, ["integration_read", "read", "read users"]);

/** Config + webhook status reads (GET /config, /webhook-status). */
export const canAttendanceIntegrationConfigRead = () =>
  checkAnyPermission(permissionType.ATTENDANCE, ["integration_read", "read users"]);

/** Machine user list (GET /machine-users). */
export const canAttendanceMachineUsersRead = () =>
  checkAnyPermission(permissionType.ATTENDANCE, ["integration_read", "read users"]);

export const encryptData = (data) => {
  const key = CRYPTO.enc.Utf8.parse(environment.API_ENC_KEY);
  const iv = CRYPTO.enc.Utf8.parse(environment.API_ENV_IV);

  const ciphertext = CRYPTO.AES.encrypt(JSON.stringify(data), key, {
    iv: iv,
    mode: CRYPTO.mode.CBC,
  });
  return ciphertext.toString();
};

export const decryptData = (cipher) => {
  const key = CRYPTO.enc.Utf8.parse(environment.API_ENC_KEY);
  const iv = CRYPTO.enc.Utf8.parse(environment.API_ENV_IV);

  const decrypted = CRYPTO.AES.decrypt(cipher, key, {
    iv: iv,
    mode: CRYPTO.mode.CBC,
  });

  const decryptedData = decrypted.toString(CRYPTO.enc.Utf8);
  return JSON.parse(decryptedData);
};

export const getClientSource = () => {
  let source = window.location.hostname.split('.')[1];
  return source === "sattvatech" ? "sattva" : "tcs"
}